import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Partner } from '../api/types'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { Empty, ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { initials, relativeDays, roleList, shortDate, titleCase, usd } from '../lib/format'
import { tint } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

const GROUPS = [
  { key: 'agents', label: 'Agents' },
  { key: 'title', label: 'Title' },
  { key: 'closing', label: 'Closing' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'appraisal', label: 'Appraisal' },
  { key: 'all', label: 'All third parties' },
]

const GRID = '29px 1.5fr 1.1fr 0.6fr 0.85fr 0.6fr 0.8fr 110px'

export function Partners() {
  const [group, setGroup] = useState('agents')
  const [report, setReport] = useState<Partner | null>(null)
  const nav = useNavigate()
  const { data, error, loading, reload } = useAsync(() => api.partners(group), [group])

  const partners = data?.partners ?? []
  const volume = partners.reduce((a, p) => a + (p.volume ?? 0), 0)
  const max = Math.max(1, ...partners.map((p) => p.volume || 0))

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="h1">Referral partners</h1>
          <p className="subline">
            {partners.length} sources · <span className="mono">{usd(volume)}</span> attached ·
            read from the loan file, never typed in
          </p>
        </div>
      </div>

      <div className="smartlist">
        {GROUPS.map((g) => (
          <button key={g.key} className={`slchip${group === g.key ? ' active' : ''}`} onClick={() => setGroup(g.key)}>
            {g.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading && !data ? (
          <Loading />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : partners.length === 0 ? (
          <Empty title="No partners in this role yet">They appear as soon as a loan file names one.</Empty>
        ) : (
          <>
            <div className="gt-head" style={{ gridTemplateColumns: GRID }}>
              <span />
              <span>Partner</span>
              <span>Roles</span>
              <span className="right">Loans</span>
              <span className="right">Volume</span>
              <span className="right">Funded</span>
              <span>Last loan</span>
              <span />
            </div>
            {partners.map((p) => (
              <div
                className="gt-row"
                key={p.person_key}
                role="button"
                tabIndex={0}
                style={{ gridTemplateColumns: GRID, cursor: 'pointer' }}
                onClick={() => nav(`/partners/${encodeURIComponent(p.person_key)}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') nav(`/partners/${encodeURIComponent(p.person_key)}`) }}
              >
                <span className="gt-av" style={tint(p.name)}>{initials(p.name)}</span>
                <span style={{ minWidth: 0 }}>
                  <span className="gt-name">{p.name ?? '—'}</span>
                  <span className="gt-sub">{p.company ?? p.email ?? '—'}</span>
                </span>
                <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {roleList(p.roles).map((r) => <span className="badge t-neutral" key={r}>{titleCase(r)}</span>)}
                </span>
                <span className="right mono">{p.loans}</span>
                <span className="right" style={{ minWidth: 0 }}>
                  <span className="mono" style={{ fontSize: 12.5, display: 'block' }}>{usd(p.volume)}</span>
                  <span className="bar-track" style={{ height: 4, marginTop: 4, display: 'block' }}>
                    <span className="bar-fill" style={{ width: `${Math.round((100 * (p.volume || 0)) / max)}%`, background: 'var(--accent)', display: 'block' }} />
                  </span>
                </span>
                <span className="right">
                  {p.funded ? <span className="badge t-positive">{p.funded}</span> : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                </span>
                <span className="gt-sub">{relativeDays(p.last_activity)}</span>
                <span className="right">
                  <button className="btn sm" onClick={(e) => { e.stopPropagation(); setReport(p) }} title="Status report of their loans">
                    <Icon name="summarize" size={15} /> Report
                  </button>
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      {report ? <PartnerReportModal partner={report} onClose={() => setReport(null)} /> : null}
    </div>
  )
}

/** "Where are my buyers' loans?" — the question agents text an officer daily.
 *  Preview the report, then email it; the send goes through the same consent gate
 *  as every other message. */
function PartnerReportModal({ partner, onClose }: { partner: Partner; onClose: () => void }) {
  const [days, setDays] = useState(90)
  const [busy, setBusy] = useState(false)
  const { data, loading, error } = useAsync(() => api.partnerReport(partner.person_key, days), [partner.person_key, days])
  const toast = useToast()

  async function send() {
    setBusy(true)
    try {
      const r = await api.sendPartnerReport(partner.person_key, days)
      const msg = { sent: 'Report emailed', simulated: 'Logged — messaging is simulated, nothing was sent', blocked: `Not sent: ${r.reason}`, failed: `Couldn't send: ${r.reason}` }[r.status]
      toast(msg, { tone: r.status === 'sent' ? 'positive' : r.status === 'simulated' ? 'warning' : 'danger' })
      if (r.status === 'sent' || r.status === 'simulated') onClose()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const rows = data?.rows ?? []
  return (
    <Modal open onClose={onClose} width={680} title={`Loan report · ${partner.name ?? partner.company ?? 'Partner'}`}
      subtitle={partner.email ? `Emails ${partner.email}` : 'No email on file for this partner'}
      footer={<>
        <button className="btn" onClick={onClose}>Close</button>
        <button className="btn primary" onClick={send} disabled={!partner.email || !rows.length || busy}>
          <Icon name="send" size={17} /> {busy ? 'Sending…' : 'Email report'}
        </button>
      </>}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[30, 90, 180].map((d) => (
          <button key={d} className={`slchip${days === d ? ' active' : ''}`} onClick={() => setDays(d)}>Last {d} days</button>
        ))}
      </div>
      {loading && !data ? <Loading /> : error ? <ErrorState error={error} /> : rows.length === 0 ? (
        <p className="quiet">No loans with this partner in the last {days} days.</p>
      ) : (
        <div>
          {rows.map((r) => (
            <div className="condrow" key={`${r.loan_id}-${r.role}`}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{r.borrower ?? r.loan_number}</span>
                <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{titleCase(r.role)} · {r.property_city ?? '—'} · updated {shortDate(r.status_at)}</span>
              </span>
              <span className="badge t-accent">{titleCase(r.status)}</span>
              <span className="mono" style={{ fontSize: 12.5, minWidth: 90, textAlign: 'right' }}>{usd(r.loan_amount)}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}
